import React, { useCallback, useMemo } from 'react';
import VisuallyHidden from '@atlaskit/visually-hidden';
import {
	ActionsWrapper,
	LeftActionsWrapper,
	RightActionsWrapper,
} from '@atlassian/jira-business-app-wrapper/src/ui/actions-wrapper/index.tsx';
import { FormatButton } from '@atlassian/jira-business-conditional-formatting-sidebar/src/ui/format-rules-sidebar/index.tsx';
import { useExpandCollapseAllItems } from '@atlassian/jira-business-expand-collapse-all-items-controller/src/index.tsx';
import {
	BUSINESS_FIELD_TYPES,
	SOFTWARE_FIELD_TYPES,
} from '@atlassian/jira-business-filters/src/common/constants.tsx';
import { useHideDoneItems } from '@atlassian/jira-business-filters/src/controllers/hide-done-items/index.tsx';
import { Filters } from '@atlassian/jira-business-filters/src/ui/filters/index.tsx';
import { SimpleSearchField } from '@atlassian/jira-business-filters/src/ui/simple-search-field/index.tsx';
import AddPeopleWithAvatar from '@atlassian/jira-business-invite-people/src/ui/add-people-with-avatars/index.tsx';
import { JWMShareButton } from '@atlassian/jira-business-invite-people/src/ui/jwm-share-button/index.tsx';
import MoreMenu from '@atlassian/jira-business-more-menu/src/ui/more-menu/index.tsx';
import SettingsMenu from '@atlassian/jira-business-settings-menu/src/ui/settings-menu/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { integrationTypes } from '@atlassian/jira-share-button/src/constants.tsx';
import { FilterDropdownListNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/list-nudge-tour/filter-dropdown-list/async.tsx';
import { MoreDropdownListNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/list-nudge-tour/more-dropdown-list/async.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useJWMListFeatures } from '../../../controllers/features-context/index.tsx';
import { useGroupBy } from '../../../controllers/group-by/index.tsx';
import { useHierarchyConfig } from '../../../controllers/hierarchy/hierarchy-config/index.tsx';
import GroupByDropdown from './group-by-dropdown/index.tsx';
import IssueFieldStatisticsButtonWithPulse from './issue-field-statistics-button/index.tsx';
import messages from './messages.tsx';
import { useFilterProps } from './utils.tsx';
import { ListMoreMenu } from './more-menu/ListMoreMenu.tsx';

export const HeaderActions = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		inviteButtonInteraction,
		isShareButtonEnabled,
		isPresenceEnabled,
		hasFilterCustomFields,
		isSoftwareFieldsEnabled,
	} = useJWMListFeatures();

	const [hideDoneItems, setHideDoneItems] = useHideDoneItems();

	const [
		{ hasExpandedAllItems, hasCollapsedAllItems },
		{ toggleExpandAllItems, collapseAllItems },
	] = useExpandCollapseAllItems({
		view: 'list',
	});

	const [groupByFieldId] = useGroupBy();

	const isGrouping = groupByFieldId != null;

	const { showFlatList } = useHierarchyConfig();

	const { formatMessage } = useIntl();

	const { hiddenQuickFilters, allowedAliasFieldId } = useFilterProps();

	const projectType = getWillShowNav4() && isSoftwareFieldsEnabled ? 'software' : 'business';

	const hasFeedbackButton =
		isVisualRefreshEnabled() && fg('visual-refresh_drop_5') ? false : projectType === 'software';

	const shareButton = (
		<JWMShareButton
			shareTitle={messages.shareTitle}
			integrationType={integrationTypes.LIST}
			shareContentType="list"
		/>
	);

	const handleExpandCollapseAllItems = useCallback(
		(operation: 'collapse' | 'expand') => {
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'expandCollapseAll', {
				operation,
			});
			if (operation === 'collapse') {
				collapseAllItems();
			} else if (operation === 'expand') {
				toggleExpandAllItems();
			}
		},
		[collapseAllItems, createAnalyticsEvent, toggleExpandAllItems],
	);

	const searchBarLabelId = 'header-actions-search-field-label';
	const searchBar = (
		<>
			<VisuallyHidden id={searchBarLabelId}>{formatMessage(messages.searchLabel)}</VisuallyHidden>
			<SimpleSearchField
				placeholder={messages.simpleSearch}
				view="list"
				describedbyId={searchBarLabelId}
			/>
		</>
	);

	const addPeopleWithPresence = (
		<AddPeopleWithAvatar
			view="list"
			isPresenceEnabledInContext={isPresenceEnabled}
			isOpenNewTabOnClick={inviteButtonInteraction === 'openNewTab'}
			allowCustomFields={hasFilterCustomFields}
			allowedAliasFieldId={allowedAliasFieldId}
			hasInviteButton={!isVisualRefreshEnabled()}
		/>
	);

	const shareActionButton = isShareButtonEnabled && shareButton;
	const allowedFieldTypes = useMemo(
		() =>
			isSoftwareFieldsEnabled
				? new Set([...BUSINESS_FIELD_TYPES, ...SOFTWARE_FIELD_TYPES])
				: undefined,
		[isSoftwareFieldsEnabled],
	);

	const filters = (
		<FilterDropdownListNudgeAsync>
			<Filters
				view="list"
				allowedFieldTypes={allowedFieldTypes}
				allowCustomFields={hasFilterCustomFields}
				allowedAliasFieldId={allowedAliasFieldId}
				hiddenQuickFilters={hiddenQuickFilters}
			/>
		</FilterDropdownListNudgeAsync>
	);

	const groupByDropdown = <GroupByDropdown />;

	const formatRulesBtn = expValEquals('list_view_conditional_formatting', 'cohort', 'variation') ? (
		<FormatButton />
	) : null;

	const fieldStatistics = expValEquals('helios_list_view_experiment_m2', 'cohort', 'variation') ? (
		<IssueFieldStatisticsButtonWithPulse />
	) : null;

	const visualRefreshEnabled = isVisualRefreshEnabled();

	const settingsMenu = visualRefreshEnabled && (
		<SettingsMenu
			hideDoneItems={Boolean(hideDoneItems)}
			onChangeHideDoneItems={setHideDoneItems}
			onExpandCollapseAllItems={handleExpandCollapseAllItems}
			isExpandCollapseButtonEnabled={!showFlatList || isGrouping}
			hasExpandedAllItems={hasExpandedAllItems}
			hasCollapsedAllItems={hasCollapsedAllItems}
		/>
	);

	const moreMenuOld =
		(visualRefreshEnabled && hasFeedbackButton) || !visualRefreshEnabled ? (
			<MoreDropdownListNudgeAsync>
				<MoreMenu
					hideDoneItems={Boolean(hideDoneItems)}
					onChangeHideDoneItems={setHideDoneItems}
					onExpandCollapseAllItems={handleExpandCollapseAllItems}
					isExpandCollapseButtonEnabled={!showFlatList || isGrouping}
					hasExpandedAllItems={hasExpandedAllItems}
					hasCollapsedAllItems={hasCollapsedAllItems}
					hasFeedbackButton={hasFeedbackButton}
					projectType={projectType}
					hideItemsMenuOptions={visualRefreshEnabled}
				/>
			</MoreDropdownListNudgeAsync>
		) : null;

	const showMenu =
		isVisualRefreshEnabled() &&
		fg('visual-refresh-list-more-menu') &&
		(expValEquals('list_view_conditional_formatting', 'cohort', 'variation') ||
			expValEquals('helios_list_view_experiment_m2', 'cohort', 'variation'));

	return (
		<ActionsWrapper>
			<LeftActionsWrapper>
				{searchBar}
				{addPeopleWithPresence}
				{isVisualRefreshEnabled() && filters}
			</LeftActionsWrapper>
			<RightActionsWrapper>
				{!isVisualRefreshEnabled() && !getWillShowNav4() && shareActionButton}
				{!isVisualRefreshEnabled() && filters}
				{groupByDropdown}
				{(!isVisualRefreshEnabled() ||
					(isVisualRefreshEnabled() && !fg('visual-refresh-list-more-menu'))) &&
					formatRulesBtn}
				{(!isVisualRefreshEnabled() ||
					(isVisualRefreshEnabled() && !fg('visual-refresh-list-more-menu'))) &&
					fieldStatistics}
				{settingsMenu}
				{(!isVisualRefreshEnabled() ||
					(isVisualRefreshEnabled() && !fg('visual-refresh-list-more-menu'))) &&
					moreMenuOld}
				{showMenu && <ListMoreMenu />}
			</RightActionsWrapper>
		</ActionsWrapper>
	);
};

export default HeaderActions;
